import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import "./schemePdfModal.scss";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "iframe",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export default function PdfSchemeModal({
  open,
  items,
  index = 0,
  onClose,
  onChangeIndex,
}) {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const reactId = useId();
  const titleId = `pdf-scheme-modal-title-${reactId.replace(/:/g, "")}`;

  const safeItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const safeIndex = Number.isInteger(index) ? index : 0;
  const current = useMemo(
    () => safeItems[safeIndex] || null,
    [safeItems, safeIndex]
  );

  const hasPrev = safeIndex > 0;
  const hasNext = safeIndex < safeItems.length - 1;
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [pdfViewerEnabled, setPdfViewerEnabled] = useState(() =>
    typeof navigator === "undefined" || navigator.pdfViewerEnabled !== false
  );

  useEffect(() => {
    if (!open || !current?.url) return undefined;

    setLoading(true);
    setLoadFailed(false);
    const viewerEnabled =
      typeof navigator === "undefined" || navigator.pdfViewerEnabled !== false;
    setPdfViewerEnabled(viewerEnabled);

    if (!viewerEnabled) {
      setLoading(false);
      return undefined;
    }

    const loadingTimeout = window.setTimeout(() => {
      setLoading(false);
    }, 15000);

    return () => window.clearTimeout(loadingTimeout);
  }, [open, current?.id, current?.url]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocusedRef.current = document.activeElement;
    const focusFrame = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(focusFrame);
      const previous = previouslyFocusedRef.current;
      if (previous && typeof previous.focus === "function" && document.contains(previous)) {
        previous.focus();
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
        return;
      }

      if (!event.altKey && !event.ctrlKey && !event.metaKey) {
        if (event.key === "ArrowLeft" && hasPrev) {
          event.preventDefault();
          onChangeIndex?.(safeIndex - 1);
          return;
        }

        if (event.key === "ArrowRight" && hasNext) {
          event.preventDefault();
          onChangeIndex?.(safeIndex + 1);
          return;
        }
      }

      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = Array.from(
        dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR)
      ).filter((element) => !element.hasAttribute("aria-hidden"));

      if (!focusable.length) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !dialogRef.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, hasPrev, hasNext, safeIndex, onClose, onChangeIndex]);

  if (!open || !current) return null;

  const downloadUrl = current.downloadUrl || current.url;
  const showFallback = pdfViewerEnabled === false || loadFailed;
  const title = current.title || "PDF схема";

  return (
    <div
      className="pdf-scheme-modal"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <section
        ref={dialogRef}
        className="pdf-scheme-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        lang="uz-Cyrl"
      >
        <header className="pdf-scheme-modal__header">
          <div className="pdf-scheme-modal__heading">
            <h2 id={titleId}>{title}</h2>
            <span className="pdf-scheme-modal__counter" aria-live="polite">
              {safeIndex + 1} / {safeItems.length}
            </span>
          </div>

          <div className="pdf-scheme-modal__actions">
            <a
              className="pdf-scheme-modal__action"
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              download
            >
              PDF юклаб олиш
            </a>
            <a
              className="pdf-scheme-modal__action"
              href={current.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Алоҳида очиш
            </a>
            <button
              ref={closeButtonRef}
              className="pdf-scheme-modal__action pdf-scheme-modal__action--close"
              type="button"
              onClick={onClose}
            >
              Ёпиш
            </button>
          </div>
        </header>

        <div
          className="pdf-scheme-modal__viewer"
          aria-busy={!showFallback && loading}
        >
          {showFallback ? (
            <div className="pdf-scheme-modal__fallback" role="alert">
              <div className="pdf-scheme-modal__file-icon" aria-hidden="true">
                PDF
              </div>
              <p>Ушбу браузер PDF ҳужжатни модал ойнада кўрсата олмайди.</p>
              <div className="pdf-scheme-modal__fallback-actions">
                <a
                  className="pdf-scheme-modal__action"
                  href={current.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Алоҳида очиш
                </a>
                <a
                  className="pdf-scheme-modal__action"
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                >
                  PDF юклаб олиш
                </a>
              </div>
            </div>
          ) : (
            <>
              {loading && (
                <div className="pdf-scheme-modal__loading" role="status" aria-live="polite">
                  <span className="pdf-scheme-modal__spinner" aria-hidden="true" />
                  <span>PDF юкланмоқда...</span>
                </div>
              )}
              <iframe
                key={current.id || current.url}
                className={`pdf-scheme-modal__frame${loading ? " is-loading" : ""}`}
                src={current.url}
                title={title}
                loading="lazy"
                onLoad={() => setLoading(false)}
                onError={() => {
                  setLoading(false);
                  setLoadFailed(true);
                }}
              />
            </>
          )}
        </div>

        <footer className="pdf-scheme-modal__navigation" aria-label="Схемалар навигацияси">
          <button
            className="pdf-scheme-modal__nav-button"
            type="button"
            disabled={!hasPrev}
            onClick={() => hasPrev && onChangeIndex?.(safeIndex - 1)}
          >
            Олдинги схема
          </button>
          <button
            className="pdf-scheme-modal__nav-button"
            type="button"
            disabled={!hasNext}
            onClick={() => hasNext && onChangeIndex?.(safeIndex + 1)}
          >
            Кейинги схема
          </button>
        </footer>
      </section>
    </div>
  );
}
